import React, { useState, useEffect } from 'react';
import {
  Card, Table, DatePicker, Input, Space, Typography, Tag, Button, Tabs, Spin, Empty,
} from 'antd';
import { ReloadOutlined, HistoryOutlined, TeamOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { apiClient } from '../../config/apiClient';
import MainLayout from '../layout/layout';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const MOBILE_BREAKPOINT = 768;

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= MOBILE_BREAKPOINT);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
};

const HistorialFichadas = () => {
  const isMobile = useIsMobile();
  const [dataJugadores, setDataJugadores] = useState([]);
  const [dataProfesores, setDataProfesores] = useState([]);
  const [loadingJugadores, setLoadingJugadores] = useState(false);
  const [loadingProfesores, setLoadingProfesores] = useState(false);
  const [totalJugadores, setTotalJugadores] = useState(0);
  const [totalProfesores, setTotalProfesores] = useState(0);
  const [pageJugadores, setPageJugadores] = useState(1);
  const [pageProfesores, setPageProfesores] = useState(1);
  const [nombre, setNombre] = useState('');
  const [rango, setRango] = useState([dayjs().subtract(30, 'day'), dayjs()]);

  const buildParams = (p) => {
    const params = new URLSearchParams({ page: p, pageSize: isMobile ? 15 : 20 });
    if (nombre.trim()) params.set('nombre', nombre.trim());
    if (rango?.[0]) params.set('fechaDesde', rango[0].format('YYYY-MM-DD'));
    if (rango?.[1]) params.set('fechaHasta', rango[1].format('YYYY-MM-DD'));
    return params;
  };

  const cargarJugadores = async (p = pageJugadores) => {
    setLoadingJugadores(true);
    try {
      const res = await apiClient.get(`/fichada/historial?${buildParams(p)}`);
      if (res.ok) {
        const json = await res.json();
        setDataJugadores(json.data || []);
        setTotalJugadores(json.total || 0);
      }
    } finally {
      setLoadingJugadores(false);
    }
  };

  const cargarProfesores = async (p = pageProfesores) => {
    setLoadingProfesores(true);
    try {
      const res = await apiClient.get(`/fichada/historial-profesores?${buildParams(p)}`);
      if (res.ok) {
        const json = await res.json();
        setDataProfesores(json.data || []);
        setTotalProfesores(json.total || 0);
      }
    } finally {
      setLoadingProfesores(false);
    }
  };

  const cargar = (pJug = 1, pProf = 1) => {
    cargarJugadores(pJug);
    cargarProfesores(pProf);
  };

  useEffect(() => {
    setPageJugadores(1);
    setPageProfesores(1);
    cargar(1, 1);
  }, [rango, isMobile]);

  const buscar = () => {
    setPageJugadores(1);
    setPageProfesores(1);
    cargar(1, 1);
  };

  const tipoColorJugador = { JUGADOR: 'blue', ADMIN: 'purple', FICHADA: 'cyan' };
  const tipoColorProfesor = { PROFESOR: 'blue', ADMIN: 'purple', FICHADA: 'cyan' };

  const renderJugadorCard = (r) => (
    <Card key={r.fic_id} size="small" style={{ borderRadius: 12, borderLeft: '4px solid #1890ff' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <div>
          <Text strong style={{ fontSize: 16 }}>{r.alu_nombre} {r.alu_apellido}</Text>
          <div>
            <ClockCircleOutlined style={{ marginRight: 6, color: '#888' }} />
            <Text type="secondary">
              {dayjs(r.fic_fecha).format('DD/MM/YYYY')} · {dayjs(r.fic_hora).format('HH:mm')}
            </Text>
          </div>
        </div>
        <Tag color={tipoColorJugador[r.fic_tipo] || 'default'}>{r.fic_tipo}</Tag>
      </div>
      {r.tur_hora_desde && (
        <Text style={{ display: 'block', marginBottom: 4 }}>
          Clase: {r.tur_hora_desde?.substring(0, 5)} – {r.tur_hora_hasta?.substring(0, 5)}
          {r.cancha ? ` · ${r.cancha}` : ''}
        </Text>
      )}
      <Space wrap size={4}>
        {r.alu_documento && <Tag>DNI {r.alu_documento}</Tag>}
        {r.alu_telefono && <Tag>{r.alu_telefono}</Tag>}
      </Space>
    </Card>
  );

  const renderProfesorCard = (r) => (
    <Card key={r.fip_id} size="small" style={{ borderRadius: 12, borderLeft: '4px solid #722ed1' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Text strong style={{ fontSize: 16 }}>{r.pro_nombre} {r.pro_apellido}</Text>
          <div>
            <ClockCircleOutlined style={{ marginRight: 6, color: '#888' }} />
            <Text type="secondary">
              {dayjs(r.fip_fecha).format('DD/MM/YYYY')} · {dayjs(r.fip_hora).format('HH:mm')}
            </Text>
          </div>
          {r.pro_usuario && <Text type="secondary" style={{ fontSize: 12 }}>@{r.pro_usuario}</Text>}
        </div>
        <Tag color={tipoColorProfesor[r.fip_tipo] || 'default'}>{r.fip_tipo}</Tag>
      </div>
    </Card>
  );

  const paginacionMobile = (current, total, pageSize, onChange) => (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: 16,
      gap: 8,
    }}>
      <Button disabled={current <= 1} onClick={() => onChange(current - 1)} block>
        Anterior
      </Button>
      <Text type="secondary" style={{ whiteSpace: 'nowrap', fontSize: 13 }}>
        {current} / {Math.max(1, Math.ceil(total / pageSize))}
      </Text>
      <Button
        disabled={current >= Math.ceil(total / pageSize)}
        onClick={() => onChange(current + 1)}
        block
      >
        Siguiente
      </Button>
    </div>
  );

  const columnsJugadores = [
    {
      title: 'Fecha',
      dataIndex: 'fic_fecha',
      key: 'fecha',
      width: 100,
      render: (v) => dayjs(v).format('DD/MM/YY'),
    },
    {
      title: 'Hora',
      dataIndex: 'fic_hora',
      key: 'hora',
      width: 70,
      render: (v) => dayjs(v).format('HH:mm'),
    },
    {
      title: 'Jugador',
      key: 'jugador',
      render: (_, r) => `${r.alu_nombre} ${r.alu_apellido}`,
    },
    { title: 'DNI', dataIndex: 'alu_documento', key: 'dni', responsive: ['md'], render: (v) => v || '-' },
    { title: 'Tel.', dataIndex: 'alu_telefono', key: 'tel', responsive: ['lg'], render: (v) => v || '-' },
    {
      title: 'Clase',
      key: 'clase',
      responsive: ['md'],
      render: (_, r) => r.tur_hora_desde
        ? `${r.tur_hora_desde?.substring(0, 5)}-${r.tur_hora_hasta?.substring(0, 5)}`
        : '-',
    },
    { title: 'Cancha', dataIndex: 'cancha', key: 'cancha', responsive: ['lg'], render: (v) => v || '-' },
    {
      title: 'Tipo',
      dataIndex: 'fic_tipo',
      key: 'tipo',
      width: 90,
      render: (v) => <Tag color={tipoColorJugador[v] || 'default'}>{v}</Tag>,
    },
  ];

  const columnsProfesores = [
    {
      title: 'Fecha',
      dataIndex: 'fip_fecha',
      key: 'fecha',
      width: 100,
      render: (v) => dayjs(v).format('DD/MM/YY'),
    },
    {
      title: 'Hora',
      dataIndex: 'fip_hora',
      key: 'hora',
      width: 70,
      render: (v) => dayjs(v).format('HH:mm'),
    },
    {
      title: 'Profesor',
      key: 'profesor',
      render: (_, r) => `${r.pro_nombre} ${r.pro_apellido}`,
    },
    { title: 'Usuario', dataIndex: 'pro_usuario', key: 'usuario', responsive: ['md'], render: (v) => v || '-' },
    {
      title: 'Tipo',
      dataIndex: 'fip_tipo',
      key: 'tipo',
      width: 90,
      render: (v) => <Tag color={tipoColorProfesor[v] || 'default'}>{v}</Tag>,
    },
  ];

  const pageSize = isMobile ? 15 : 20;

  const listaJugadoresMobile = (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {loadingJugadores ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : dataJugadores.length === 0 ? (
          <Empty description="Sin fichadas en el período" />
        ) : (
          dataJugadores.map(renderJugadorCard)
        )}
      </div>
      {!loadingJugadores && totalJugadores > 0 && paginacionMobile(
        pageJugadores,
        totalJugadores,
        pageSize,
        (p) => { setPageJugadores(p); cargarJugadores(p); },
      )}
    </>
  );

  const listaProfesoresMobile = (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {loadingProfesores ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : dataProfesores.length === 0 ? (
          <Empty description="Sin ingresos en el período" />
        ) : (
          dataProfesores.map(renderProfesorCard)
        )}
      </div>
      {!loadingProfesores && totalProfesores > 0 && paginacionMobile(
        pageProfesores,
        totalProfesores,
        pageSize,
        (p) => { setPageProfesores(p); cargarProfesores(p); },
      )}
    </>
  );

  return (
    <MainLayout>
      <div style={{ padding: isMobile ? '0 0 16px' : undefined }}>
        <Card styles={{ body: { padding: isMobile ? 12 : 24 } }}>
          <Title level={isMobile ? 4 : 3} style={{ marginTop: 0 }}>
            <HistoryOutlined /> Historial de fichadas
          </Title>

          <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }} size="middle">
            <RangePicker
              value={rango}
              onChange={(v) => v && setRango(v)}
              format="DD/MM/YYYY"
              size={isMobile ? 'large' : 'middle'}
              style={{ width: '100%' }}
              inputReadOnly
            />
            <Input.Search
              placeholder="Buscar por nombre..."
              allowClear
              enterButton
              onSearch={buscar}
              onChange={(e) => setNombre(e.target.value)}
              size={isMobile ? 'large' : 'middle'}
              style={{ width: '100%' }}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={() => cargar(pageJugadores, pageProfesores)}
              block={isMobile}
              size={isMobile ? 'large' : 'middle'}
            >
              Actualizar
            </Button>
          </Space>

          <Tabs
            size={isMobile ? 'large' : 'middle'}
            centered={isMobile}
            items={[
              {
                key: 'jugadores',
                label: `Jugadores (${totalJugadores})`,
                children: isMobile ? listaJugadoresMobile : (
                  <Table
                    columns={columnsJugadores}
                    dataSource={dataJugadores}
                    rowKey="fic_id"
                    loading={loadingJugadores}
                    size="middle"
                    pagination={{
                      current: pageJugadores,
                      total: totalJugadores,
                      pageSize,
                      onChange: (p) => { setPageJugadores(p); cargarJugadores(p); },
                      showTotal: (t) => `${t} registros`,
                    }}
                    scroll={{ x: 800 }}
                  />
                ),
              },
              {
                key: 'profesores',
                label: <span><TeamOutlined /> Profesores ({totalProfesores})</span>,
                children: isMobile ? listaProfesoresMobile : (
                  <Table
                    columns={columnsProfesores}
                    dataSource={dataProfesores}
                    rowKey="fip_id"
                    loading={loadingProfesores}
                    size="middle"
                    pagination={{
                      current: pageProfesores,
                      total: totalProfesores,
                      pageSize,
                      onChange: (p) => { setPageProfesores(p); cargarProfesores(p); },
                      showTotal: (t) => `${t} registros`,
                    }}
                    scroll={{ x: 500 }}
                  />
                ),
              },
            ]}
          />
        </Card>
      </div>
    </MainLayout>
  );
};

export default HistorialFichadas;
